import React from 'react';
import { AlertCircle, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

function ProgressRow({ label, ratio, active, done }) {
  const percent = Math.round(Math.min(1, Math.max(0, ratio)) * 100);

  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="flex items-center gap-1.5 text-[#2d1810]">
          {done ? (
            <CheckCircle2 className="h-3.5 w-3.5 text-[#6b8f5d]" />
          ) : active ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-[#8b6f47]" />
          ) : (
            <span className="h-3.5 w-3.5" />
          )}
          {label}
        </span>
        <span className="text-[#8b6f47]">{percent}%</span>
      </div>
      <Progress value={percent} className="h-2 bg-[#efe5d8]" />
    </div>
  );
}

/**
 * S3へのアップロード中の進捗。ここだけはブラウザが送信しているので閉じられない。
 * 変換以降の進捗は受付完了画面（AdminVideoCreateAccepted）で扱う。
 */
export default function AdminVideoCreateProgress({
  phase,
  uploadProgress,
  hasMaterial,
  errorMessage,
  onBackToConfirm,
}) {
  const isUploading = phase === 'uploading';
  const isFailed = phase === 'failed';

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-[#e8dccd] bg-white p-5">
        <div className="mb-4 flex items-center gap-2">
          {isFailed ? (
            <AlertCircle className="h-5 w-5 text-[#b4462f]" />
          ) : (
            <Loader2 className="h-5 w-5 animate-spin text-[#8b6f47]" />
          )}
          <h2 className="text-sm font-semibold text-[#2d1810]">
            {isFailed ? '処理に失敗しました' : 'アップロード中'}
          </h2>
        </div>

        {!isFailed && (
          <p className="mb-4 text-xs text-[#6b5540]">
            ファイルを送信しています。この画面を閉じたり再読み込みすると中断されます。
          </p>
        )}

        <div className="space-y-3">
          <ProgressRow
            label="動画ファイル"
            ratio={uploadProgress.video}
            active={isUploading && uploadProgress.video < 1}
            done={uploadProgress.video >= 1}
          />
          <ProgressRow
            label="サムネイル画像"
            ratio={uploadProgress.thumbnail}
            active={isUploading && uploadProgress.video >= 1 && uploadProgress.thumbnail < 1}
            done={uploadProgress.thumbnail >= 1}
          />
          {hasMaterial && (
            <ProgressRow
              label="配布資料"
              ratio={uploadProgress.material}
              active={isUploading && uploadProgress.thumbnail >= 1 && uploadProgress.material < 1}
              done={uploadProgress.material >= 1}
            />
          )}
        </div>
      </div>

      {isFailed && (
        <>
          <div className="flex items-start gap-2 rounded-xl border border-[#e5c4bb] bg-[#fdf3f1] px-4 py-3 text-sm text-[#b4462f]">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div className="min-w-0">
              <p className="break-words">{errorMessage}</p>
              <p className="mt-1 text-xs">
                動画は登録されていません。アップロード済みの一時ファイルはサーバー側で削除されます。
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onBackToConfirm}
              className="gap-2 border-[#d4c4b0] text-[#5d4529] hover:bg-[#f5ede3]"
            >
              <ArrowLeft className="h-4 w-4" />
              入力内容に戻る
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
